import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/libs/utils/cn';
import {
  MAP_SEARCH_WIDTH_CLASS,
  MAP_TOP_BAR_COMPACT_ATTR,
  MAP_TOP_BAR_INSET_PX,
} from './mapBarGeometry';

const SCROLL_ARROW_CLASS =
  'absolute top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#e6e9e8] bg-white text-[#0B2936] shadow-[0_4px_8px_rgba(0,0,0,0.15)]';

const NARROWED_ARROW_CLASS = 'border-brand ring-2 ring-brand/10';
const NO_OVERFLOW = { left: false, right: false, leftNarrowed: false, rightNarrowed: false };
/** `gap-2` between the search column, the owner filter and the pills. */
const ROW_GAP_PX = 8;
/** Extra room required before leaving compact, so a borderline row does not flicker. */
const COMPACT_EXIT_SLACK_PX = 16;

interface MapTopBarProps {
  renderSearch: (api: { onResultsOpenChange: (open: boolean) => void }) => ReactNode;
  renderTools: (api: { compact: boolean }) => ReactNode;
  owner?: ReactNode;
  filters: ReactNode;
}

/**
 * Search, owner and layer pills share one row, 20px from the left, with the tools 8px under the
 * search. The pills take the width left of the search and scroll in it once they no longer fit,
 * with an arrow on each side that still hides pills. Once that width can no longer show the owner
 * filter and the first pill, the bar folds: the tools become one button beside the search and the
 * pills move to a line under it, as wide as the map leaves. The tools hide while search results are
 * open. The chrome carries `data-top-bar-compact` while folded, so the cards under it drop to the
 * taller bar.
 */
export function MapTopBar({ renderSearch, renderTools, owner, filters }: MapTopBarProps) {
  const [compact, setCompact] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(NO_OVERFLOW);

  // Fold once the room beside the search cannot show the owner filter and the first pill whole;
  // unfold once it can again, with slack. Widths are read live: a toggled chip changes a pill's
  // leftover count.
  const fit = useCallback(() => {
    const bar = barRef.current;
    const search = searchRef.current;
    const line = lineRef.current;
    if (!bar || !search || !line) return;
    const pills = Array.from(line.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );
    const first = pills.slice(0, owner ? 2 : 1);
    if (first.length === 0 || first.every((pill) => pill.offsetWidth === 0)) return;
    const needed =
      first.reduce((width, pill) => width + pill.offsetWidth, 0) + (owner ? ROW_GAP_PX : 0);
    const beside =
      (bar.parentElement?.clientWidth ?? 0) -
      MAP_TOP_BAR_INSET_PX -
      search.offsetWidth -
      ROW_GAP_PX;
    if (compact) {
      if (needed + COMPACT_EXIT_SLACK_PX <= beside) setCompact(false);
    } else if (needed > line.clientWidth) {
      setCompact(true);
    }
  }, [compact, owner]);

  useLayoutEffect(fit);

  useEffect(() => {
    const host = barRef.current?.parentElement;
    if (!host) return;
    const observer = new ResizeObserver(fit);
    observer.observe(host);
    window.addEventListener('resize', fit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [fit]);

  useEffect(() => {
    const host = barRef.current?.parentElement;
    if (!host) return;
    host.toggleAttribute(MAP_TOP_BAR_COMPACT_ATTR, compact);
    return () => host.removeAttribute(MAP_TOP_BAR_COMPACT_ATTR);
  }, [compact]);

  // A pill that carries an applied filter (`data-narrowed`) turns the arrow hiding it green.
  const sync = useCallback(() => {
    const line = lineRef.current;
    if (!line) return;
    const { scrollLeft, clientWidth, scrollWidth } = line;
    const next = {
      ...NO_OVERFLOW,
      left: scrollLeft > 1,
      right: scrollWidth - clientWidth - scrollLeft > 1,
    };
    for (const pill of line.querySelectorAll<HTMLElement>('[data-narrowed="true"]')) {
      if (pill.offsetLeft + pill.offsetWidth <= scrollLeft + 1) next.leftNarrowed = true;
      if (pill.offsetLeft >= scrollLeft + clientWidth - 1) next.rightNarrowed = true;
    }
    setOverflow((current) =>
      (Object.keys(next) as (keyof typeof next)[]).every((key) => current[key] === next[key])
        ? current
        : next,
    );
  }, []);

  // Every render: a toggled chip can change which pills are narrowed.
  useLayoutEffect(sync);

  useEffect(() => {
    const line = lineRef.current;
    if (!line) return;
    line.addEventListener('scroll', sync);
    const observer = new ResizeObserver(sync);
    observer.observe(line);
    return () => {
      line.removeEventListener('scroll', sync);
      observer.disconnect();
    };
  }, [compact, sync]);

  const tools = !resultsOpen && renderTools({ compact });

  const filterLine = (
    <div className="relative min-w-0 flex-1">
      <div
        ref={lineRef}
        data-testid="map-filter-line"
        className="flex flex-nowrap gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {owner}
        {filters}
      </div>
      {overflow.left && (
        <button
          type="button"
          aria-label="Previous filters"
          onClick={() => lineRef.current?.scrollBy({ left: -160, behavior: 'smooth' })}
          className={cn(
            SCROLL_ARROW_CLASS,
            'left-0',
            overflow.leftNarrowed && NARROWED_ARROW_CLASS,
          )}
        >
          <ChevronLeft className="size-4" aria-hidden />
        </button>
      )}
      {overflow.right && (
        <button
          type="button"
          aria-label="More filters"
          onClick={() => lineRef.current?.scrollBy({ left: 160, behavior: 'smooth' })}
          className={cn(
            SCROLL_ARROW_CLASS,
            'right-0',
            overflow.rightNarrowed && NARROWED_ARROW_CLASS,
          )}
        >
          <ChevronRight className="size-4" aria-hidden />
        </button>
      )}
    </div>
  );

  return (
    <div
      ref={barRef}
      data-testid="map-top-bar"
      className="absolute top-[22px] right-4 left-5 z-30 flex items-start gap-2"
    >
      <div className={cn('flex min-w-0 flex-col gap-2', compact && 'w-full')}>
        <div className="flex items-center gap-2">
          <div ref={searchRef} className={MAP_SEARCH_WIDTH_CLASS}>
            {renderSearch({ onResultsOpenChange: setResultsOpen })}
          </div>
          {compact && tools}
        </div>
        {!compact && tools}
        {compact && filterLine}
      </div>
      {!compact && filterLine}
    </div>
  );
}
